/**
 * Formats a number of bytes into a human-readable string (e.g., 1.25 MB).
 *
 * @param bytes - The number of bytes to format
 * @param decimals - The number of decimal places to include (default: 2)
 * @returns A formatted string
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

/**
 * Parses a JSON string, returning a fallback value on falsy input or parse error.
 *
 * Replaces the hand-rolled `try { x = JSON.parse(raw || '[]') } catch { x = [] }`
 * idiom: a falsy `raw` yields the fallback, a parse error yields the fallback,
 * otherwise the parsed value is returned (cast to T).
 *
 * @param raw - The raw JSON string (or undefined)
 * @param fallback - The value returned when raw is falsy or unparseable
 * @returns The parsed value, or the fallback
 */
export function parseJsonOr<T>(raw: string | undefined, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Programmatically opens a file selection dialog and returns the selected files.
 *
 * @param options - Configuration for the file selection (accept, multiple)
 * @returns A promise that resolves to the selected FileList or File[]
 */
export function selectFiles(
  options: { accept?: string; multiple?: boolean } = {},
): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    if (options.accept) input.accept = options.accept;
    if (options.multiple) input.multiple = true;

    input.onchange = () => {
      const files = input.files ? Array.from(input.files) : [];
      resolve(files);
      document.body.removeChild(input);
    };

    input.style.display = "none";
    document.body.appendChild(input);
    input.click();
  });
}
